import type { EngagementProvider } from "@/provider/EngagementProvider";
import type {
	EngagementProviderBootInput,
	ProviderRuntimeSnapshot,
} from "@/provider/ProviderTypes";
import type { ProviderRuntimeStore } from "@/provider/ProviderRuntimeStore";

export type SupportedProviderId = "captainup";

export function normalizeIntegrationType(
	providerBootInput: EngagementProviderBootInput | null
): string | null {
	if (!providerBootInput?.integrationType) {
		return null;
	}

	const normalized = providerBootInput.integrationType.trim().toLowerCase();

	return normalized.length > 0 ? normalized : null;
}

export function normalizeBootInput(
	providerBootEligible: boolean,
	providerBootInput: EngagementProviderBootInput | null,
	integrationType: string | null
): EngagementProviderBootInput | null {
	if (!providerBootEligible || !providerBootInput || !integrationType) {
		return null;
	}

	return {
		...providerBootInput,
		integrationType,
	};
}

export function isSupportedProviderId(
	integrationType: string | null
): integrationType is SupportedProviderId {
	return integrationType === "captainup";
}

export function isRuntimeReadyForProvider(
	snapshot: ProviderRuntimeSnapshot,
	providerId: string,
	providerReady: boolean
): boolean {
	return (
		providerReady &&
		snapshot.phase === "ready" &&
		snapshot.providerId === providerId
	);
}

export function shouldResetRuntime(
	providerBootEligible: boolean,
	normalizedBootInput: EngagementProviderBootInput | null,
	integrationType: string | null
): boolean {
	return !providerBootEligible || !normalizedBootInput || !integrationType;
}

export function assertEligibleRuntimeInputs(
	normalizedBootInput: EngagementProviderBootInput | null,
	integrationType: string | null
): asserts normalizedBootInput is EngagementProviderBootInput {
	if (!normalizedBootInput || !integrationType) {
		throw new Error(
			"[EF ProviderSupervisor] expected eligible runtime inputs to exist after prerequisite guard"
		);
	}
}

export function markRuntimeEligible(
	runtimeStore: ProviderRuntimeStore,
	integrationType: string,
	normalizedBootInput: EngagementProviderBootInput,
	requestedBootGeneration: number
): void {
	runtimeStore.setProviderId(integrationType);
	runtimeStore.markEligible(normalizedBootInput, requestedBootGeneration);
}

export function markUnsupportedProviderDegraded(
	runtimeStore: ProviderRuntimeStore
): void {
	runtimeStore.markDegraded("unsupported_provider_integration_type");
}

export function shouldMarkBooting(
	bootEnabled: boolean,
	hasNormalizedBootInput: boolean,
	activeBootGeneration: number,
	inFlightBootGeneration: number | null,
	requestedBootGeneration: number,
	snapshot: ProviderRuntimeSnapshot,
	providerId: string
): boolean {
	if (!bootEnabled || !hasNormalizedBootInput) {
		return false;
	}

	if (
		inFlightBootGeneration !== requestedBootGeneration ||
		activeBootGeneration !== requestedBootGeneration
	) {
		return false;
	}

	if (snapshot.phase === "booting" && snapshot.providerId === providerId) {
		return false;
	}

	return true;
}

export function markRuntimeBooting(
	runtimeStore: ProviderRuntimeStore,
	requestedBootGeneration: number
): void {
	runtimeStore.beginBoot(requestedBootGeneration);
}

export function shouldMarkReady(
	bootEnabled: boolean,
	hasNormalizedBootInput: boolean,
	provider: EngagementProvider | null,
	providerReady: boolean,
	readyBootGeneration: number | null,
	requestedBootGeneration: number,
	snapshot: ProviderRuntimeSnapshot,
	currentRuntimeProvider: EngagementProvider | null,
	providerId: string
): boolean {
	if (!bootEnabled || !hasNormalizedBootInput) {
		return false;
	}

	if (!providerReady || !provider) {
		return false;
	}

	if (readyBootGeneration !== requestedBootGeneration) {
		return false;
	}

	if (
		snapshot.phase === "ready" &&
		snapshot.providerId === providerId &&
		currentRuntimeProvider === provider
	) {
		return false;
	}

	return true;
}

export function markRuntimeReady(
	runtimeStore: ProviderRuntimeStore,
	provider: EngagementProvider,
	requestedBootGeneration: number
): void {
	runtimeStore.setBootedProvider(provider);
	runtimeStore.markReady(requestedBootGeneration);
}

export function shouldMarkFailed(
	bootEnabled: boolean,
	hasNormalizedBootInput: boolean,
	lastBootError: Error | null,
	inFlightBootGeneration: number | null,
	snapshot: ProviderRuntimeSnapshot,
	providerId: string
): boolean {
	if (!bootEnabled || !hasNormalizedBootInput) {
		return false;
	}

	if (!lastBootError) {
		return false;
	}

	if (inFlightBootGeneration !== null) {
		return false;
	}

	if (
		snapshot.phase === "failed" &&
		snapshot.providerId === providerId &&
		snapshot.error?.message === lastBootError.message
	) {
		return false;
	}

	return true;
}

export function markRuntimeFailed(
	runtimeStore: ProviderRuntimeStore,
	lastBootError: Error
): void {
	runtimeStore.setBootedProvider(null);
	runtimeStore.markFailed("provider_boot_failed", lastBootError);
}